import { getAuth } from "firebase/auth";
import { logger } from '../utils/logger';
import { authAxios } from '../utils/apiConfig';

const chatAxios = authAxios;

/**
 * Send a message in a chat conversation.
 * Returns immediately with status "processing" — poll getChatStatus() for progress.
 * @param {string} message - The user's message
 * @param {string|null} conversationId - Existing conversation ID, or null for new
 * @returns {Promise<{success: boolean, conversation_id: string, status: string}>}
 */
export async function sendChatMessage(message, conversationId = null) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const body = { message };
    if (conversationId) {
      body.conversation_id = conversationId;
    }

    const response = await chatAxios.post('/chat/send', body, {
      params: { user: user.email },
    });

    return response.data;
  } catch (error) {
    logger.error("Error in sendChatMessage:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.detail || error.response.data.message || 'Server error');
    }
    throw error;
  }
}

/**
 * Poll agent progress for a conversation.
 * @param {string} conversationId
 * @returns {Promise<{status: string, tools_called: Array, reply: string, error: string}>}
 */
export async function getChatStatus(conversationId) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('Authentication required');
    }

    const response = await chatAxios.get('/chat/status', {
      params: { user: user.email, conversation_id: conversationId },
    });

    return response.data;
  } catch (error) {
    logger.error("Error in getChatStatus:", error);
    if (error.response) {
      throw new Error(error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

/**
 * List user's chat conversations.
 * @returns {Promise<{success: boolean, conversations: Array}>}
 */
export async function listConversations() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await chatAxios.get('/chat/conversations', {
      params: { user: user.email },
    });

    return response.data;
  } catch (error) {
    logger.error("Error in listConversations:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.detail || error.response.data.message || 'Server error');
    }
    throw error;
  }
}

/**
 * Get a single conversation with all messages.
 * @param {string} conversationId
 * @returns {Promise<{success: boolean, conversation: object}>}
 */
export async function getConversation(conversationId) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await chatAxios.get('/chat/conversation', {
      params: { user: user.email, conversation_id: conversationId },
    });

    return response.data;
  } catch (error) {
    logger.error("Error in getConversation:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.detail || error.response.data.message || 'Server error');
    }
    throw error;
  }
}

const chatService = {
  sendChatMessage,
  getChatStatus,
  listConversations,
  getConversation,
};

export default chatService;
